import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useDebounce } from 'use-debounce'

import { apiGetDevices } from '@/features/devices/api/api-get-devices'
import type { Device } from '@/features/devices/types/device'
import { apiCreateJob } from '@/features/jobs/api/api-create-job'
import { apiGetLocations } from '@/features/jobs/api/api-get-locations'
import { apiGetSiteWorksPartners } from '@/features/jobs/api/api-get-site-works-partners'
import { useLocationSearch } from '@/features/jobs/hooks/use-location-search'
import { formatJobSeverity } from '@/features/jobs/lib/format-job-severity'
import { formatJobType } from '@/features/jobs/lib/format-job-type'
import type { JobSeverity, JobType } from '@/features/jobs/types/job'
import {
  emptyJobFormValues,
  toCreateJobPayload,
  type JobFormValues,
} from '@/features/jobs/types/job-form'
import type { SearchableLocation } from '@/features/jobs/types/searchable-location'
import { ApiError } from '@/lib/api/api-error'
import type { FilterOption } from '@/lib/api/types'
import {
  Button,
  ComboboxField,
  InputField,
  RadioGroupField,
  SelectField,
  TextareaField,
  type SelectOption,
} from '@/shared/components'

const SEARCH_DEBOUNCE_MS = 300

type RaiseJobFormProps = {
  onCancel: () => void
  onSuccess: () => void
}

const FORM_FIELD_KEYS = [
  'location_id',
  'job_type',
  'device_attached_id',
  'reason_for_remediation',
  'assigned_partner_id',
  'severity',
  'site_reference',
  'latitude',
  'longitude',
  'road_name',
  'town',
  'county',
  'asset_location',
  'what3words',
  'postcode'
] as const

type FormFieldKey = (typeof FORM_FIELD_KEYS)[number]

const ERROR_KEY_ALIASES: Record<string, FormFieldKey> = {
  assigned_partner: 'assigned_partner_id',
  device_attached: 'device_attached_id',
}

const normalizeFieldErrors = (errors: Record<string, string[]>) => {
  const normalized: Record<string, string[]> = {}

  for (const [key, messages] of Object.entries(errors)) {
    const mappedKey = ERROR_KEY_ALIASES[key] ?? key
    normalized[mappedKey] = [...(normalized[mappedKey] ?? []), ...messages]
  }

  return normalized
}

const jobTypeOptions = (['new_installation', 'remediation'] as JobType[]).map(
  (jobType) => ({
    value: jobType,
    label: formatJobType(jobType),
  })
)

const severityOptions: SelectOption[] = (
  ['expedited', 'urgent', 'bau'] as JobSeverity[]
).map((severity) => ({
  value: severity,
  label: formatJobSeverity(severity),
}))

const toSelectOptions = (options: FilterOption[]): SelectOption[] =>
  options.map((option) => ({
    value: String(option.id),
    label: option.label,
  }))

const firstFieldError = (
  fieldErrors: Record<string, string[]>,
  key: FormFieldKey
) => fieldErrors[key]?.[0]

const unmappedErrorMessages = (fieldErrors: Record<string, string[]>) => {
  const knownKeys = new Set<string>(FORM_FIELD_KEYS)

  return Object.entries(fieldErrors)
    .filter(([key]) => !knownKeys.has(key))
    .flatMap(([, messages]) => messages)
}

const LOCATION_FIELD_KEYS = [
  'location_id',
  'site_reference',
  'latitude',
  'longitude',
  'road_name',
  'town',
  'county',
  'asset_location',
  'what3words',
  'postcode',
] as const

const nullableToString = (value: string | null | undefined) => value ?? ''

const formatDeviceOptionLabel = (device: Device) => device.serial_number

const locationFieldsFromCatalog = (
  location: SearchableLocation
): Pick<JobFormValues, (typeof LOCATION_FIELD_KEYS)[number]> => ({
  location_id: location.location_id,
  site_reference: nullableToString(location.site_reference),
  latitude: nullableToString(location.latitude),
  longitude: nullableToString(location.longitude),
  road_name: nullableToString(location.road_name),
  town: nullableToString(location.town),
  county: nullableToString(location.county),
  asset_location: nullableToString(location.asset_location),
  what3words: nullableToString(location.what3words),
  postcode: nullableToString(location.postcode),
})

export const RaiseJobForm = ({ onCancel, onSuccess }: RaiseJobFormProps) => {
  const [draft, setDraft] = useState<JobFormValues>(emptyJobFormValues)
  const [partnerOptions, setPartnerOptions] = useState<FilterOption[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [deviceInputValue, setDeviceInputValue] = useState('')
  const [selectedDeviceLabel, setSelectedDeviceLabel] = useState<string | null>(null)
  const [hasOpenedDevicePicker, setHasOpenedDevicePicker] = useState(false)
  const [isLoadingPartners, setIsLoadingPartners] = useState(true)
  const [isSearchingDevices, setIsSearchingDevices] = useState(false)
  const [optionsError, setOptionsError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    locationSelectOptions,
    selectedLocationId,
    locationInputValue,
    isSearchingLocations,
    searchError,
    selectLocation,
    changeLocationInput,
    openLocationPicker,
  } = useLocationSearch({ search: apiGetLocations })
  const [debouncedDeviceQuery] = useDebounce(deviceInputValue, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    const loadPartners = async () => {
      setIsLoadingPartners(true)
      setOptionsError(null)

      try {
        const partners = await apiGetSiteWorksPartners()
        setPartnerOptions(partners)
      } catch (loadError) {
        setOptionsError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load form options'
        )
      } finally {
        setIsLoadingPartners(false)
      }
    }

    void loadPartners()
  }, [])

  useEffect(() => {
    if (!hasOpenedDevicePicker) {
      return
    }

    const query = debouncedDeviceQuery.trim()

    if (selectedDeviceLabel !== null && query === selectedDeviceLabel) {
      return
    }

    let cancelled = false

    const searchDevices = async () => {
      setIsSearchingDevices(true)

      try {
        const result = await apiGetDevices(
          { serial_number: query, location: '' },
          1
        )
        if (!cancelled) {
          setDevices(result.data)
        }
      } catch (searchError) {
        if (!cancelled) {
          setDevices([])
          setOptionsError(
            searchError instanceof Error
              ? searchError.message
              : 'Failed to search devices'
          )
        }
      } finally {
        if (!cancelled) {
          setIsSearchingDevices(false)
        }
      }
    }

    void searchDevices()

    return () => {
      cancelled = true
    }
  }, [debouncedDeviceQuery, hasOpenedDevicePicker, selectedDeviceLabel])

  const partnerSelectOptions = useMemo(
    () => toSelectOptions(partnerOptions),
    [partnerOptions]
  )

  const deviceSelectOptions = useMemo<SelectOption[]>(
    () =>
      devices.map((device) => ({
        value: String(device.id),
        label: formatDeviceOptionLabel(device),
      })),
    [devices]
  )

  const updateField = <K extends keyof JobFormValues>(
    key: K,
    value: JobFormValues[K]
  ) => {
    setDraft((current) => ({ ...current, [key]: value }))
    setFieldErrors((current) => {
      if (!(key in current)) {
        return current
      }

      const next = { ...current }
      delete next[key]
      return next
    })
  }

  const handleLocationSelect = (value: string | undefined) => {
    const location = selectLocation(value)

    if (!location) {
      return
    }

    setDraft((current) => ({
      ...current,
      ...locationFieldsFromCatalog(location),
    }))
    setFieldErrors((current) => {
      const next = { ...current }
      for (const key of LOCATION_FIELD_KEYS) {
        delete next[key]
      }
      return next
    })
  }

  const handleDeviceSelect = (value: string | undefined) => {
    if (!value) {
      setSelectedDeviceLabel(null)
      setDraft((current) => ({
        ...current,
        device_attached_id: undefined,
      }))
      return
    }

    const device = devices.find((item) => String(item.id) === value)
    if (!device) {
      return
    }

    setSelectedDeviceLabel(device.serial_number)
    setDeviceInputValue(device.serial_number)
    setDraft((current) => ({
      ...current,
      device_attached_id: String(device.id),
    }))
    setFieldErrors((current) => {
      const next = { ...current }
      delete next.device_attached_id
      return next
    })
  }

  const handleDeviceInputChange = (nextValue: string) => {
    setDeviceInputValue(nextValue)
    setOptionsError(null)

    if (selectedDeviceLabel !== null && nextValue.trim() !== selectedDeviceLabel) {
      setSelectedDeviceLabel(null)
      setDraft((current) => ({
        ...current,
        device_attached_id: undefined,
      }))
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setFormError(null)
    setFieldErrors({})

    try {
      await apiCreateJob(toCreateJobPayload(draft))
      onSuccess()
    } catch (submitError) {
      if (submitError instanceof ApiError) {
        const normalizedErrors = normalizeFieldErrors(submitError.errors)
        setFieldErrors(normalizedErrors)
        const unmapped = unmappedErrorMessages(normalizedErrors)
        setFormError(
          unmapped.length > 0
            ? [submitError.message, ...unmapped].join(' ')
            : submitError.message
        )
      } else {
        setFormError(
          submitError instanceof Error
            ? submitError.message
            : 'Failed to create job'
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="flex flex-col gap-8 p-6" onSubmit={handleSubmit} noValidate>
      <h1 className='font-bold'>Please fill in all the details</h1>

      {optionsError || searchError ? (
        <p className="text-sm text-destructive" role="alert">
          {optionsError ?? searchError}
        </p>
      ) : null}

      {formError ? (
        <p className="text-sm text-destructive" role="alert">
          {formError}
        </p>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-2">
        <div className="flex flex-col gap-6">
          <ComboboxField
            id="raise-job-location-id"
            label="Location ID"
            layout="inline"
            value={selectedLocationId}
            onValueChange={handleLocationSelect}
            inputValue={locationInputValue}
            onInputValueChange={changeLocationInput}
            onFocus={openLocationPicker}
            options={locationSelectOptions}
            placeholder="Search for a location"
            loading={isSearchingLocations}
            error={firstFieldError(fieldErrors, 'location_id')}
          />

          <RadioGroupField
            id="raise-job-job-type"
            name="job_type"
            label="Job type"
            layout="inline"
            value={draft.job_type}
            onValueChange={(job_type) =>
              updateField('job_type', job_type as JobType)
            }
            options={jobTypeOptions}
            error={firstFieldError(fieldErrors, 'job_type')}
          />

          <ComboboxField
            id="raise-job-device-attached"
            label="Device attached"
            layout="inline"
            value={draft.device_attached_id}
            onValueChange={handleDeviceSelect}
            inputValue={deviceInputValue}
            onInputValueChange={handleDeviceInputChange}
            onFocus={() => setHasOpenedDevicePicker(true)}
            options={deviceSelectOptions}
            placeholder="Search for a device"
            loading={isSearchingDevices}
            error={firstFieldError(fieldErrors, 'device_attached_id')}
          />

          {draft.job_type === 'remediation' ? (
            <TextareaField
              id="raise-job-reason-for-remediation"
              label="Reason for remediation"
              placeholder="Enter reason for remediation"
              layout="inline"
              value={draft.reason_for_remediation}
              onChange={(reason_for_remediation) =>
                updateField('reason_for_remediation', reason_for_remediation)
              }
              error={firstFieldError(fieldErrors, 'reason_for_remediation')}
            />
          ) : null}

          <SelectField
            id="raise-job-site-works-partner"
            label="SiteWorks partner"
            layout="inline"
            value={draft.assigned_partner_id}
            onValueChange={(assigned_partner_id) =>
              updateField('assigned_partner_id', assigned_partner_id)
            }
            options={partnerSelectOptions}
            placeholder="Select a partner"
            disabled={isLoadingPartners}
            error={firstFieldError(fieldErrors, 'assigned_partner_id')}
          />

          <SelectField
            id="raise-job-severity"
            label="Severity"
            layout="inline"
            value={draft.severity}
            onValueChange={(severity) =>
              updateField('severity', severity as JobSeverity | undefined)
            }
            options={severityOptions}
            placeholder="Select severity"
            error={firstFieldError(fieldErrors, 'severity')}
          />
        </div>

        <div className="flex flex-col gap-6">
          <InputField
            id="raise-job-site-reference"
            label="Site reference"
            placeholder="Enter site reference"
            layout="inline"
            value={draft.site_reference}
            onChange={(site_reference) =>
              updateField('site_reference', site_reference)
            }
            error={firstFieldError(fieldErrors, 'site_reference')}
          />

          <InputField
            id="raise-job-latitude"
            label="Latitude"
            layout="inline"
            placeholder="Enter coordinates"
            value={draft.latitude}
            onChange={(latitude) => updateField('latitude', latitude)}
            error={firstFieldError(fieldErrors, 'latitude')}
          />

          <InputField
            id="raise-job-longitude"
            label="Longitude"
            layout="inline"
            placeholder="Enter coordinates"
            value={draft.longitude}
            onChange={(longitude) => updateField('longitude', longitude)}
            error={firstFieldError(fieldErrors, 'longitude')}
          />

          <InputField
            id="raise-job-what3words"
            label="what3words"
            layout="inline"
            placeholder="first.second.third"
            value={draft.what3words}
            onChange={(what3words) => updateField('what3words', what3words)}
            error={firstFieldError(fieldErrors, 'what3words')}
          />

          <InputField
            id="raise-job-asset-location"
            label="Asset location"
            layout="inline"
            placeholder="Enter description"
            value={draft.asset_location}
            onChange={(asset_location) =>
              updateField('asset_location', asset_location)
            }
            error={firstFieldError(fieldErrors, 'asset_location')}
          />

          <InputField
            id="raise-job-road-name"
            label="Road name"
            placeholder="Enter road name"
            layout="inline"
            value={draft.road_name}
            onChange={(road_name) => updateField('road_name', road_name)}
            error={firstFieldError(fieldErrors, 'road_name')}
          />

          <InputField
            id="raise-job-town"
            label="Town"
            placeholder="Enter town"
            layout="inline"
            value={draft.town}
            onChange={(town) => updateField('town', town)}
            error={firstFieldError(fieldErrors, 'town')}
          />

          <InputField
            id="raise-job-county"
            label="County"
            placeholder="Enter county "
            layout="inline"
            value={draft.county}
            onChange={(county) => updateField('county', county)}
            error={firstFieldError(fieldErrors, 'county')}
          />

          <InputField
            id="raise-job-postcode"
            label="Postcode"
            placeholder="Enter postcode"
            layout="inline"
            value={draft.postcode}
            onChange={(postcode) => updateField('postcode', postcode)}
            error={firstFieldError(fieldErrors, 'postcode')}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-outline/40 pt-6">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <Button type="submit" variant="action" disabled={isSubmitting}>
          Create job
        </Button>
      </div>
    </form>
  )
}
